/**
 * Admin › Carteiras pré-pagas and Grupos de motoristas (W8-DRIVER-PREPAID-WALLET, D459, ADR-0074; finding C-05; owner
 * answer D435 §5.0 item 4) — the pure view-model behind the two screens, over `@vello/api-client` `WalletAdminApi`.
 *
 * - who may do what (`wallet:view`, `wallet:manage` = the maker, `wallet:approve` = the checker, `driver_group:manage`);
 * - the tone of a standing and of a change, and whether THIS operator may decide a change (never the one who proposed
 *   it — the server refuses `SELF_APPROVAL` anyway, the screen hides the button and says why);
 * - exact per-currency totals of what the market's drivers owe (bigint, never summed across currencies);
 * - the draft checks of a package and an adjustment before anything is posted, and the words for each stable refusal.
 * Nothing here names a country, a currency, a PSP or an amount.
 */
import { type DriverGroupView, type WalletAdjustmentView, type WalletCeilingChangeView, type WalletMarketRow, type WalletStandingView, WALLET_ADJUSTMENT_REASON_CODES } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

export type WalletTone = 'neutral' | 'success' | 'warning' | 'danger';

export function walletAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean; readonly mayApprove: boolean; readonly mayManageGroups: boolean } {
  if (grants === undefined) return { mayView: true, mayManage: true, mayApprove: true, mayManageGroups: true };
  return {
    mayView: grantsAllow(grants, { permission: 'wallet:view', target: 'PLATFORM' }),
    mayManage: grantsAllow(grants, { permission: 'wallet:manage', target: 'PLATFORM' }),
    mayApprove: grantsAllow(grants, { permission: 'wallet:approve', target: 'PLATFORM' }),
    mayManageGroups: grantsAllow(grants, { permission: 'driver_group:manage', target: 'PLATFORM' }),
  };
}

export const WALLET_STATUSES = ['OK', 'WARN', 'BLOCKED', 'UNGOVERNED'] as const;

export function walletStatusTone(status: string): WalletTone {
  return status === 'OK' ? 'success' : status === 'WARN' ? 'warning' : status === 'BLOCKED' ? 'danger' : 'neutral';
}

export function walletChangeTone(status: string): WalletTone {
  return status === 'APPLIED' ? 'success' : status === 'REJECTED' ? 'danger' : 'warning';
}

/** Why this operator may not approve a change (or undefined when he may). */
export function walletApproveBlock(change: Pick<WalletAdjustmentView | WalletCeilingChangeView, 'proposedBy' | 'status'>, operatorId: string | undefined, mayApprove: boolean, selfApproval = false): 'NOT_PENDING' | 'NO_PERMISSION' | 'OWN_PROPOSAL' | undefined {
  if (change.status !== 'PENDING') return 'NOT_PENDING';
  if (!mayApprove) return 'NO_PERMISSION';
  if (!selfApproval && operatorId !== undefined && change.proposedBy === operatorId) return 'OWN_PROPOSAL';
  return undefined;
}

/** What the market's drivers owe, per currency (exact), and how many are warned / blocked. */
export function summarizeWallets(rows: readonly WalletMarketRow[]): { readonly debtByCurrency: readonly { readonly currency: string; readonly amountMinor: bigint }[]; readonly warned: number; readonly blocked: number; readonly inDebt: number } {
  const debt = new Map<string, bigint>();
  let warned = 0;
  let blocked = 0;
  let inDebt = 0;
  for (const r of rows) {
    const s: WalletStandingView = r.standing;
    if (/^\d+$/.test(s.debt.amountMinor)) {
      const d = BigInt(s.debt.amountMinor);
      if (d > 0n) {
        inDebt += 1;
        debt.set(s.debt.currency, (debt.get(s.debt.currency) ?? 0n) + d);
      }
    }
    if (s.status === 'WARN') warned += 1;
    if (s.status === 'BLOCKED') blocked += 1;
  }
  return { debtByCurrency: [...debt.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([currency, amountMinor]) => ({ currency, amountMinor })), warned, blocked, inDebt };
}

export type PackageDraftIssue = 'CREDIT_REQUIRED' | 'PRICE_REQUIRED' | 'PRICE_ABOVE_CREDIT';

/** A package draft: a positive credit, a positive price not above it (the difference is the discount). */
export function packageDraftIssue(creditMinor: bigint | undefined, priceMinor: bigint | undefined): PackageDraftIssue | undefined {
  if (creditMinor === undefined || creditMinor <= 0n) return 'CREDIT_REQUIRED';
  if (priceMinor === undefined || priceMinor <= 0n) return 'PRICE_REQUIRED';
  if (priceMinor > creditMinor) return 'PRICE_ABOVE_CREDIT';
  return undefined;
}

/** The discount of a package in whole percent (0 when the price is the credit). */
export function packageDiscountPercent(creditMinor: bigint, priceMinor: bigint): number {
  if (creditMinor <= 0n || priceMinor >= creditMinor) return 0;
  return Number(((creditMinor - priceMinor) * 100n) / creditMinor);
}

export type AdjustmentDraftIssue = 'DRIVER_REQUIRED' | 'AMOUNT_REQUIRED' | 'REASON_REQUIRED' | 'NOTE_REQUIRED';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** An adjustment draft: a driver, a positive amount, a stable reason and a note (the checker reads why). */
export function adjustmentDraftIssue(d: { readonly driverId: string; readonly amountMinor: bigint | undefined; readonly reasonCode: string; readonly note: string }): AdjustmentDraftIssue | undefined {
  if (!UUID.test(d.driverId.trim())) return 'DRIVER_REQUIRED';
  if (d.amountMinor === undefined || d.amountMinor <= 0n) return 'AMOUNT_REQUIRED';
  if (!(WALLET_ADJUSTMENT_REASON_CODES as readonly string[]).includes(d.reasonCode)) return 'REASON_REQUIRED';
  if (d.note.trim().length < 3) return 'NOTE_REQUIRED';
  return undefined;
}

export function isDriverId(v: string): boolean {
  return UUID.test(v.trim());
}

/** Active groups first, then by name. */
export function sortGroups(groups: readonly DriverGroupView[]): readonly DriverGroupView[] {
  return [...groups].sort((a, b) => (a.status === b.status ? a.name.localeCompare(b.name) : a.status === 'ACTIVE' ? -1 : 1));
}

